import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import Input from './Input';
import Select from './Select';
import Button from './Buttons';
import InviteLinkBox from './InviteLinkBox';
import { useApi } from '../hooks/useApi';
import { fetchTenantInviteRoleOptions, inviteTenantUser } from '../services/userRolesService';

/**
 * Modal for inviting a new user at the tenant level.
 *
 * Props:
 *   isOpen    - boolean
 *   onClose   - () => void
 *   onSuccess - () => void  (optional, called after successful invite; when email delivery is
 *               disabled it fires when the user closes the dialog, after they have seen the link)
 */
const InviteTenantUserModal = ({ isOpen, onClose, onSuccess }) => {
    const apiCall = useApi();
    const [formData, setFormData] = useState({ email: '', fullName: '', role: '', scope: '' });
    const [roleOptions, setRoleOptions] = useState([]);
    const [sending, setSending] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    // Set when the backend reports emailDelivery "disabled": { message, inviteUrl }
    const [disabledResult, setDisabledResult] = useState(null);

    useEffect(() => {
        if (!isOpen) return;
        setDisabledResult(null);
        setFormData({ email: '', fullName: '', role: '', scope: '' });
        setSuccessMessage('');
        setErrorMessage('');
        fetchTenantInviteRoleOptions(apiCall)
            .then(opts => setRoleOptions(opts || []))
            .catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    const handleRoleChange = (e) => {
        const compositeValue = e.target.value;
        if (!compositeValue) {
            setFormData({ ...formData, role: '', scope: '' });
            return;
        }
        const lastUnderscore = compositeValue.lastIndexOf('_');
        const role = compositeValue.substring(0, lastUnderscore);
        const scope = compositeValue.substring(lastUnderscore + 1);
        setFormData({ ...formData, role, scope });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSending(true);
        setSuccessMessage('');
        setErrorMessage('');
        try {
            const response = await inviteTenantUser(apiCall, {
                email: formData.email,
                fullName: formData.fullName,
                role: formData.role,
                scope: formData.scope,
            });
            if (response?.emailDelivery === 'disabled') {
                // No email goes out on this installation: show the link (when the backend returned one)
                // and keep the dialog open. The link stays in component state only.
                setDisabledResult({
                    message: response.message || 'Invitation created. No email was sent.',
                    inviteUrl: response.inviteUrl || '',
                });
                return;
            }
            setSuccessMessage('Invitation sent successfully! A notification email has been sent. Please check your spam/junk folder if you do not receive it in your inbox.');
            if (onSuccess) onSuccess();
            setTimeout(() => onClose(), 5000);
        } catch (err) {
            setErrorMessage('Error: ' + (err.response?.data?.message || err.message));
        } finally {
            setSending(false);
        }
    };

    const selectedComposite = formData.role && formData.scope
        ? `${formData.role}_${formData.scope}`
        : '';

    const handleDisabledClose = () => {
        setDisabledResult(null);
        if (onSuccess) onSuccess();
        onClose();
    };

    if (disabledResult) {
        return (
            <Modal isOpen={isOpen} onClose={handleDisabledClose} title="Add New Tenant User">
                <div className="space-y-4">
                    <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900">
                        {disabledResult.message}
                    </div>
                    {disabledResult.inviteUrl && <InviteLinkBox url={disabledResult.inviteUrl} />}
                    <div className="flex justify-end">
                        <Button type="button" onClick={handleDisabledClose}>
                            Close
                        </Button>
                    </div>
                </div>
            </Modal>
        );
    }

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Add New Tenant User">
            <form onSubmit={handleSubmit} className="space-y-4">
                <Input
                    label="Email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                />
                <Input
                    label="Full Name"
                    value={formData.fullName}
                    onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                    required
                />
                <Select
                    label="Role"
                    value={selectedComposite}
                    onChange={handleRoleChange}
                    placeholder="-- Select Role --"
                    options={roleOptions.map(r => ({
                        value: `${r.role}_${r.scope}`,
                        label: r.displayName,
                    }))}
                />
                {successMessage && (
                    <div className="rounded-md bg-green-50 border border-green-200 p-3 text-sm text-green-800">
                        {successMessage}
                    </div>
                )}
                {errorMessage && (
                    <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-800">
                        {errorMessage}
                    </div>
                )}
                <div className="flex justify-end space-x-3">
                    <Button type="button" variant="secondary" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button type="submit" disabled={sending || !!successMessage}>
                        {sending ? 'Sending...' : 'Send Invite'}
                    </Button>
                </div>
            </form>
        </Modal>
    );
};

export default InviteTenantUserModal;
